import { Editor, Element, Node, type NodeEntry, Transforms } from 'slate';
import { isList, isListItem, SUPPORTED_TYPES } from './guards';
import {
  type ListItemElement,
  type EditorPlugin,
  type NormalizationPlugin,
} from '../../types/aha-editor';
import NodeUtils from '../../AhaEditorUtils/NodeUtils';

// A list should only ever contain list_items .
// If a list contains a block such as a paragraph as a direct child,
// wrap it in a list_item.
const wrapInvalidListChildren: NormalizationPlugin = {
  type: SUPPORTED_TYPES,
  run: (editor, [_, path]) => {
    const children = Array.from(Node.children(editor, path));

    if (children.length === 0) {
      Transforms.insertNodes(
        editor,
        {
          type: 'list_item',
          children: [
            {
              type: 'paragraph',
              children: [{ text: '' }],
            },
          ],
        },
        { at: [...path, 0] }
      );
      return true;
    }

    for (const [childNode] of children) {
      if (!isListItem(childNode)) {
        Transforms.wrapNodes(
          editor,
          { type: 'list_item', children: [] },
          { at: path, match: (n) => n === childNode }
        );
        return true;
      }
    }
  },
};

// Wrap any non-block children of a list item in a paragraph
const wrapInvalidListItemChildren: NormalizationPlugin = {
  type: 'list_item',
  run: (editor, [_, path]) => {
    const children = Array.from(Node.children(editor, path));

    for (const [childNode, childPath] of children) {
      if (!Element.isElement(childNode) || !Editor.isBlock(editor, childNode)) {
        Transforms.wrapNodes(
          editor,
          { type: 'paragraph', children: [] },
          { at: childPath, match: (n) => n === childNode }
        );
        return true;
      }
    }
  },
};

// Sibling lists should be merged if they are of the same type.
const mergeSiblingListsOfSameType: NormalizationPlugin = {
  type: SUPPORTED_TYPES,
  run: (editor, [node, path]) => {
    if (!isList(node)) return;

    const isListOfSameType = (sibling: Node) =>
      isList(sibling) && sibling.type === node.type;

    const previousSiblingEntry = NodeUtils.previousSibling(editor, path, {
      match: isListOfSameType,
    });
    if (previousSiblingEntry) {
      Transforms.mergeNodes(editor, { at: path });
      return true;
    }

    const nextSiblingEntry = NodeUtils.nextSibling(editor, path, {
      match: isListOfSameType,
    });
    if (nextSiblingEntry) {
      const [nextSibling, nextSiblingPath] = nextSiblingEntry;
      if (Element.isElement(nextSibling) && nextSibling.children.length === 0) {
        Transforms.removeNodes(editor, { at: nextSiblingPath });
      } else {
        Transforms.mergeNodes(editor, { at: nextSiblingPath });
      }
      return true;
    }
  },
};

// A list item should always be a direct child of a list.
// If a list item is orphaned, unwrap it, leaving the contents.
const unwrapOrphanedListItems: NormalizationPlugin = {
  type: 'list_item',
  run: (editor, [_, path]) => {
    const parent = Node.parent(editor, path);

    if (Editor.isEditor(parent) || !SUPPORTED_TYPES.includes(parent.type)) {
      Transforms.unwrapNodes(editor, { at: path });
      return true;
    }
  },
};

// A list item containing only a single child that is a list should
// not be the next sibling of another list item, it should be a descendant
// of that list item.
const ensureCorrectNesting: NormalizationPlugin = {
  type: SUPPORTED_TYPES,
  run: (editor, [node, path]: NodeEntry<ListItemElement>) => {
    if (node.children.length > 1) {
      for (let i = node.children.length - 1; i > 0; i--) {
        const listItem = node.children[i];

        if (
          isListItem(listItem) &&
          listItem.children.length === 1 &&
          isList(listItem.children[0])
        ) {
          Transforms.mergeNodes(editor, {
            at: [...path, i],
          });
          const updatedItem = Editor.node(editor, [
            ...path,
            i - 1,
          ]) as NodeEntry<ListItemElement>;
          const children = updatedItem[0].children;
          // We have to merge any descendant lists here as the normalization
          // pass will not pick it up as these elements have not changed.
          mergeSiblingListsOfSameType.run(editor, [
            children[children.length - 1],
            [...path, i - 1, children.length - 1],
          ]);
          return true;
        }
      }
    }
  },
};

const ListNormalizationsPlugin: EditorPlugin = {
  name: 'List Normalizations',
  normalizations: [
    mergeSiblingListsOfSameType,
    wrapInvalidListChildren,
    wrapInvalidListItemChildren,
    unwrapOrphanedListItems,
    ensureCorrectNesting,
  ],
};

export default ListNormalizationsPlugin;
